import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useCrefazClt } from "@/app/contexts/CrefazCltContext";
import { useCrefazCltContext } from "@/app/hooks/useCrefazCltContext";
import { useCustomerCpf } from "@/app/hooks/useCustomerCpf";
import { crefazCltService } from "@/app/services/crefazCltService";
import { currentStep } from "@/app/store";
import { formatDateForInput } from "@/app/utils/formatDate";
import masks from "@/app/utils/masks";
import { useToast } from "@/view/components/ui/use-toast";

const requiredId = (message = "* obrigatório") => z.string().min(1, message);

const clienteSchema = z.object({
  nome: z.string().min(1, "* obrigatório"),
  email: z.string().min(1, "* obrigatório").email("E-mail inválido"),
  telefone: z
    .string()
    .refine((v) => v.replace(/\D/g, "").length === 11, "Telefone inválido"),
  rg: z.string().min(1, "* obrigatório"),
  rgEmissor: requiredId(),
  rgUfId: requiredId(),
  rgEmissao: z
    .string()
    .min(1, "* obrigatório")
    .refine((value) => {
      const [day, month, year] = value.split("/");

      return Boolean(day && month && year);
    }, "Data inválida"),
  sexo: requiredId(),
  estadoCivil: requiredId(),
  nomeConjuge: z.string().optional(),
  nomeMae: z.string().min(1, "* obrigatório"),
  nacionalidadeId: requiredId(),
  naturalidadeUfId: requiredId(),
  naturalidadeCidade: z.string().min(1, "* obrigatório"),
  naturalidadeCidadeId: requiredId(
    "Cidade não encontrada para a UF selecionada",
  ),
  grauInstrucaoId: requiredId(),
  pep: requiredId(),
});

const enderecoSchema = z.object({
  cep: z
    .string()
    .refine((v) => v.replace(/\D/g, "").length === 8, "CEP inválido"),
  logradouro: z.string().min(1, "* obrigatório"),
  numero: z.string().min(1, "* obrigatório"),
  bairro: z.string().min(1, "* obrigatório"),
  complemento: z.string().optional(),
  uf: z.string().length(2, "* obrigatório"),
  cidade: z.string().min(1, "* obrigatório"),
  cidadeId: requiredId("Cidade não encontrada para a UF selecionada"),
});

const referenciasSchema = z.object({
  contatos: z
    .array(
      z.object({
        grau: requiredId(),
        nome: z.string().min(2, "* obrigatório"),
        telefone: z
          .string()
          .refine(
            (v) => v.replace(/\D/g, "").length === 11,
            "Telefone inválido",
          ),
      }),
    )
    .length(2, "Informe dois contatos"),
});

const bancarioSchema = z.object({
  bancoId: requiredId(),
  agencia: z.string().min(1, "* obrigatório"),

  numero: z.string().min(2, "Informe a conta com o dígito"),
  tipoConta: requiredId(),
});

const splitAccountNumber = (value: string) => {
  const clean = value.trim().replace(/[\s.-]/g, "");

  return {
    numero: clean.slice(0, -1),
    digito: clean.slice(-1),
  };
};

const formSchema = z.object({
  cliente: clienteSchema,
  endereco: enderecoSchema,
  referencias: referenciasSchema,
  bancario: bancarioSchema,
});

export type NewCrefazCltFormData = z.infer<typeof formSchema>;

export default function useNewCrefazCltModal() {
  const [step, setStep] = useAtom(currentStep);
  const { toast } = useToast();
  const { customerData, preTransaction, selectedOffer } = useCrefazClt();

  const form = useForm<NewCrefazCltFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      referencias: {
        contatos: [
          { grau: "", nome: "", telefone: "" },
          { grau: "", nome: "", telefone: "" },
        ],
      },
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contract, setContract] = useState("");

  const cpf = customerData?.cpf;
  const { data: customer } = useCustomerCpf(cpf);

  const { items: ufs } = useCrefazCltContext("uf");

  const address = {
    ...customer?.address,
    ...(customerData?.address ?? {}),
  };

  useEffect(() => {
    if (!customer && !customerData?.address) return;

    form.reset({
      cliente: {
        nome: customer?.name ?? customerData?.name ?? "",
        email: customer?.email ?? customerData?.email ?? "",
        telefone: masks.phone(customerData?.phone || customer?.phone || ""),
        rg: customer?.rg?.number ?? "",
        rgEmissor: customer?.rg?.org ?? "",
        rgUfId: "",
        rgEmissao: customer?.rg?.date
          ? formatDateForInput(customer.rg.date)
          : "",
        sexo: customer?.gender ?? "",
        estadoCivil: customer?.maritalStatus ?? "",
        nomeConjuge: customer?.spouseName ?? "",
        nomeMae: customer?.motherName ?? "",
        nacionalidadeId: "",
        naturalidadeUfId: "",
        naturalidadeCidade: "",
        naturalidadeCidadeId: "",
        grauInstrucaoId: "",
        pep: "",
      },
      endereco: {
        cep: masks.zipcode(address.zipCode ?? ""),
        logradouro: address.street ?? "",
        numero: customer?.address?.number ?? "",
        bairro: address.neighborhood ?? "",
        complemento: customer?.address?.complement ?? "",
        uf: address.state ?? "",
        cidade: address.city ?? "",
        cidadeId: "",
      },
      referencias: {
        contatos:
          customer?.referenceContact?.length === 2
            ? customer.referenceContact.map((reference) => ({
                grau: reference.degree ?? "",
                nome: reference.name ?? "",
                telefone: masks.phone(reference.phone ?? ""),
              }))
            : [
                { grau: "", nome: "", telefone: "" },
                { grau: "", nome: "", telefone: "" },
              ],
      },
      bancario: {
        bancoId: "",
        agencia: "",
        numero: "",
        tipoConta: "",
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer, customerData?.address]);

  const submitProposal = async () => {
    const data = form.getValues();

    if (!preTransaction?.id || !selectedOffer || !cpf) return;

    try {
      setIsSubmitting(true);

      const [day, month, year] = data.cliente.rgEmissao.split("/");
      const rgUf = ufs.find(
        (uf) => String(uf.id) === data.cliente.rgUfId,
      )?.nome;
      const conta = splitAccountNumber(data.bancario.numero);

      const response = await crefazCltService.createTransaction(
        preTransaction.id,
        {
          cpf,
          cliente: {
            nome: data.cliente.nome,
            rg: data.cliente.rg,
            rgEmissor: data.cliente.rgEmissor,
            rgUfId: Number(data.cliente.rgUfId),
            rgUf,
            rgEmissao: `${year}-${month}-${day}`,
            sexo: Number(data.cliente.sexo),
            estadoCivil: Number(data.cliente.estadoCivil),
            nacionalidadeId: Number(data.cliente.nacionalidadeId),
            naturalidadeUfId: Number(data.cliente.naturalidadeUfId),
            naturalidadeCidadeId: Number(data.cliente.naturalidadeCidadeId),
            grauInstrucaoId: Number(data.cliente.grauInstrucaoId),
            nomeMae: data.cliente.nomeMae,
            nomeConjuge: data.cliente.nomeConjuge || undefined,
            pep: data.cliente.pep === "true",
          },
          contatos: {
            contato: {
              email: data.cliente.email,
              telefone: data.cliente.telefone.replace(/\D/g, ""),
            },
            referencia: data.referencias.contatos.map((reference, index) => ({
              id: index + 1,
              nome: reference.nome,
              telefone: reference.telefone.replace(/\D/g, ""),
              grau: Number(reference.grau),
            })),
          },
          endereco: {
            cep: data.endereco.cep.replace(/\D/g, ""),
            logradouro: data.endereco.logradouro,
            numero: Number(data.endereco.numero.replace(/\D/g, "")),
            bairro: data.endereco.bairro,
            complemento: data.endereco.complemento || undefined,
            cidadeId: Number(data.endereco.cidadeId),
            cidade: data.endereco.cidade,
            uf: data.endereco.uf,
          },
          bancario: {
            bancoId: data.bancario.bancoId,
            agencia: data.bancario.agencia,
            digito: conta.digito,
            numero: conta.numero,
            conta: Number(conta.numero.replace(/\D/g, "")),
            tipoConta: Number(data.bancario.tipoConta),
          },
          operacao: {
            produtoId: selectedOffer.produtoId,
            vencimento: selectedOffer.vencimento,
            valorSolicitado: selectedOffer.valorSolicitado,
            prazo: selectedOffer.prazo,
            valorParcela: selectedOffer.valorParcela,
            diaRecebimento: selectedOffer.diaRecebimentoId,
            tabelaJurosId: selectedOffer.tabelaJurosId,
          },
        },
      );

      setContract(response.contract);
      setStep(5);
    } catch (error: any) {
      toast({
        title: "Não foi possível enviar a proposta",
        description: error?.message || "Tente novamente em alguns instantes.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = {
    cliente: step === 0,
    endereco: step === 1,
    referencias: step === 2,
    bancario: step === 3,
    review: step === 4,
    sent: step === 5,
  };

  const title = [
    "Informações do cliente",
    "Endereço do cliente",
    "Referências de contato",
    "Dados bancários do cliente",
    "Revisão da proposta",
  ];

  return {
    form,
    step,
    setStep,
    steps,
    title,
    isSubmitting,
    submitProposal,
    contract,
    selectedOffer,
  };
}
